import type { WorkObjectFieldDefinition } from "../../work-object-field/model/use-work-object-fields";
import type { WorkObjectFieldValueItem } from "./use-work-object-field-values";

export type WorkObjectFieldValueDraft = string | string[];

// The single quiet placeholder for any empty field value; keeps tables and
// panels from shouting "Empty" / "No value" / "Not linked" in every cell.
export const emptyValueLabel = "—";

const valueFieldTypes = [
  "text",
  "textarea",
  "select",
  "multi_select",
  "number",
  "currency",
  "percent",
  "rating",
  "duration",
  "date",
  "date_range",
  "checkbox",
  "url",
  "email",
  "phone",
  "user_relation",
  "user_multi_relation",
  "work_object_relation",
  "work_object_multi_relation",
];

// Per-board status projections back kanban grouping and filters; the actual
// status lives on the board item, so these fields are never edited directly.
export function isBoardStatusField(field: WorkObjectFieldDefinition) {
  return (field.config as { systemKind?: string } | null | undefined)?.systemKind === "board_status";
}

// The seeded "Description" textarea predates per-item documents; the document
// is the description now, so the field only surfaces where it still has data.
export function isLegacyDescriptionField(field: WorkObjectFieldDefinition) {
  return field.origin === "default" && field.key === "description";
}

export function displayValueFieldDefinitions(fields: WorkObjectFieldDefinition[]) {
  return fields.filter((field) => field.key !== "title" && field.type !== "type_reference" && !isBoardStatusField(field));
}

export function editableValueFieldDefinitions(fields: WorkObjectFieldDefinition[]) {
  return displayValueFieldDefinitions(fields).filter((field) => valueFieldTypes.includes(field.type));
}

export function fieldAppliesToType(field: WorkObjectFieldDefinition, typeId: string | null | undefined) {
  return field.appliesToAllTypes || Boolean(typeId && field.applicableTypeIds.includes(typeId));
}

export function fieldDefinitionsForType(fields: WorkObjectFieldDefinition[], typeId: string | null | undefined) {
  return fields.filter((field) => fieldAppliesToType(field, typeId));
}

export function createFieldValueDrafts(
  fields: WorkObjectFieldDefinition[],
  values: WorkObjectFieldValueItem[] = [],
): Record<string, WorkObjectFieldValueDraft> {
  const nextDrafts: Record<string, WorkObjectFieldValueDraft> = {};
  const valuesByFieldId = new Map(values.map((value) => [value.fieldDefinitionId, value.value]));

  for (const field of displayValueFieldDefinitions(fields)) {
    const value = valuesByFieldId.get(field.id);
    nextDrafts[field.id] = isMultiValueField(field.type)
      ? (Array.isArray(value) ? value : [])
      : Array.isArray(value)
        ? value.join("\n")
        : value ?? "";
  }

  return nextDrafts;
}

export function buildFieldValueInput(
  fields: WorkObjectFieldDefinition[],
  drafts: Record<string, WorkObjectFieldValueDraft>,
): WorkObjectFieldValueItem[] {
  return editableValueFieldDefinitions(fields).map((field) => ({
    fieldDefinitionId: field.id,
    value: normalizeDraftForField(field, drafts[field.id]),
  }));
}

function normalizeDraftForField(field: WorkObjectFieldDefinition, value: WorkObjectFieldValueDraft | undefined) {
  if (isMultiValueField(field.type)) {
    if (Array.isArray(value)) {
      return value;
    }

    return (value ?? "")
      .split("\n")
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return Array.isArray(value) ? value[0] ?? "" : value ?? "";
}

export function isMultiValueField(type: WorkObjectFieldDefinition["type"]) {
  return type === "multi_select" || type === "date_range" || type === "user_multi_relation" || type === "work_object_multi_relation";
}

export function isRelationField(type: WorkObjectFieldDefinition["type"]) {
  return (
    type === "user_relation" ||
    type === "user_multi_relation" ||
    type === "work_object_relation" ||
    type === "work_object_multi_relation"
  );
}
